import { Component, Input, OnInit, EventEmitter, Output, OnDestroy } from '@angular/core';
/**
 * Router Import
 */
import { Router } from '@angular/router';
/**
 * Services Import
 */
import { NewLanguageService } from './../../dataservices/new-language/new-language.service';
import { PostApiService } from './../../dataservices/post-api.service';
import { ToastrService } from 'ngx-toastr';
import { FunCollectionService } from '../../common/common-functions/fun-collection.service';

/**
 * Rxjs Import
 */
import { Subscription } from 'rxjs';
/**
 * Interface Import
 */
import { MeetingList, RescheduledMeetingData, TimeSlotArray, RescheduleConfiguredMeeting, CalendarDateObject } from './../../common/calender-event/model/calenderList.model';
import { MeetingDetail } from '../../meeting/model/meeting.model';
import { ToastrType } from '../../onboarding/interface/global.model';

@Component({
  selector: 'app-web-calendar-actions',
  templateUrl: './web-calendar-actions.component.html',
  styleUrls: ['./web-calendar-actions.component.scss']
})
export class WebCalendarActionsComponent implements OnInit, OnDestroy {
  /**
   * Key used to provide an action on the event popup
   */
  @Input() actionModalActive = false;
  /**
   * Key used to identify what type of modal should open
   * it may be accept modal/decline modal/reschedule modal
   */
  @Input() modalType = '';
  /**
   * Input variable to store the meeting data when it comes from another component
   */
  @Input() meeting: MeetingList;
  /**
   * Input variable to store the meeting reschedule data
   */
  @Input() rescheduleMeetingData: RescheduledMeetingData;
  /**
   * Meeting Detail data.
   */
  @Input() meetingDetail: MeetingDetail;

  /**
   * Output used to emit a boolean value
   */
  @Output() closeModalData = new EventEmitter<boolean>();
  /**
   * Output used to emit update meeting detail.
   */
  @Output() meetingData = new EventEmitter<MeetingDetail>();
  /**
   * Key used to set minimum value selectable in calendar
   */
  public minDate: Date = new Date();
  /**
   * Key used to set validate startDate or not
   */
  public isValidStartDate = true;
  /**
   * Key set to false because startDate should be selectable
   */
  public is_disable_start_date = false;
  /**
   * Key used to get a time slot id value
   */
  public slotIdVal: number;
  /**
   * Key used to get timeZone string value
   */
  public timezone: string;
  /**
   * Array used to show available time slots according to selected date
   */
  public timeValue: TimeSlotArray[] = [
    { label: 'Select a time slot', value: '25:00', compareVal: 25 },
    { label: '08:00 AM - 09:00 AM', value: '8:00', compareVal: 8 },
    { label: '09:00 AM - 10:00 AM', value: '9:00', compareVal: 9 },
    { label: '10:00 AM - 11:00 AM', value: '10:00', compareVal: 10 },
    { label: '11:00 AM - 12:00 PM', value: '11:00', compareVal: 11 },
    { label: '12:00 PM - 01:00 PM', value: '12:00', compareVal: 12 },
    { label: '01:00 PM - 02:00 PM', value: '13:00', compareVal: 13 },
    { label: '02:00 PM - 03:00 PM', value: '14:00', compareVal: 14 },
    { label: '03:00 PM - 04:00 PM', value: '15:00', compareVal: 15 },
    { label: '04:00 PM - 05:00 PM', value: '16:00', compareVal: 16 }
  ];
  /**
   * Empty array which get available slots from 'timeValue' array
   */
  public slotsAvailable: TimeSlotArray[] = [];
  /**
   * Configured object before submitting to API
   */
  public rescheduleMeetingTime: RescheduleConfiguredMeeting = {
    startTime: 0,
    endTime: 0,
    reasonId: 0,
    comment: '',
    meetingId: null,
    timezone: undefined
  };
  /**
   * Key used to store date only string value
   */
  public selectedOnlyDate: string;
  /**
   * Key used to get comment entered by used
   */
  public commentValue: string;
  /**
   * Key used to get time zone text value
   */
  public timeZoneTextString: string;
  /**
   * Subscription used to unsubscribe reject meeting
   */
  meetingRejectSubs: Subscription;
  /**
   * Subscription used to unsubscribe accept meeting
   */
  meetingAcceptSubs: Subscription;
  /**
   * Subscription used to unsubscribe rescheduled meeting
   */
  meetingRescheduleSubs: Subscription;
  /**
   * Key used to reset time slot value when calendar date changes
   */
  public selectedOption: string;
  /**
   * Boolean value key used to submit form
   */
  public rescheduleFormSubmitted = false;
  /**
   * Boolean value key make active comment field when option others selected
   */
  public activeCommentForOtherOption = true;
  /**
   * Meeting Id.
   */
  public meetingId: number;
  /**
   * Variable used to store selected date value for validation
   */
  public rescheduleDateSelected: string;
  /**
   * Used if key has value then true otherwise false
   */
  public rescheduleTimeSlot = false;

  /**
   * Used to store limit in text field when giving a reason
   */
  public rescheduleReasonTextLimit = 255;

  /**
   * Function used to import services from outside of this component
   */
  constructor(
    public mls: NewLanguageService,
    private toastr: ToastrService,
    private postSer: PostApiService,
    private router: Router,
    private fcs: FunCollectionService) { }

  /**
   * Used to set values to variables when the component get initialized
   */
  ngOnInit(): void {
    this.timezone = this.getTimeZoneString();
    this.timeZoneTextString = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.slotsAvailable = this.timeValue;
    this.selectedOption = this.timeValue[0].value;
    /**
     * If current time value is greater than office hours (last 5PM) slot will be
     * 4pm-5pm and 4pm means 16:00, then min date will change to next day while rescheduling a meeting
     */
    if (new Date().getHours() >= 16) {
      const eightHours = 8 * 60 * 60 * 1000;
      this.minDate = new Date(new Date().getTime() + eightHours);
    }
  }

  /**
   * Function used to emit a false value when popup get closed
   * and navigate to relative meeting detail page
   */
  closeModal(data: string): void {
    this.closeModalData.next(false);
    this.rescheduleDateSelected = '';
    this.selectedOption = this.timeValue[0].value;
    this.rescheduleFormSubmitted = false;
    this.activeCommentForOtherOption = true;
    this.rescheduleMeetingTime.reasonId = 0;

    if (data === 'redirectToDetail') {
      this.router.navigate(['/meeting/detail/', this.meeting.productId]);
    }
  }

  /**
   * Function used to get timezone string value from current date
   */
  public getTimeZoneString(): string {
    return new Date().toTimeString().slice(9);
  }

  /**
   * Function used to decline or reject a meeting
   */
  declineMeeting(): void {
    if (this.meeting === undefined) {
      this.meetingId = this.meetingDetail.meetingId;
    } else {
      this.meetingId = this.meeting.productId;
    }
    this.meetingRejectSubs = this.postSer.meetingAction(this.meetingId, 'reject', null, null).subscribe(data => {
      if (data && data.status === 1 && data.code === 2000) {
        this.meetingData.next(data);
        this.fcs.showCustomToast({
          toastType: ToastrType.SUCCESS,
          timeout: 5000,
          message: data.msg,
          title: 'Meeting Declined Successfully'
        });

        this.closeModal('hideModal');
      }
      if (data && data.status === 0 && data.code === 2000) {
        this.fcs.showCustomToast({
          toastType: ToastrType.ERROR,
          timeout: 5000,
          message: data.error.msg,
          title: 'Error Occurred'
        });

      }
    });
  }

  /**
   * Function used to accept a meeting
   */
  acceptMeeting(callingFrom: string): void {
    if (this.meeting === undefined) {
      this.meetingId = this.meetingDetail.meetingId;
    } else {
      this.meetingId = this.meeting.productId;
    }
    this.meetingAcceptSubs = this.postSer.meetingAction(this.meetingId, 'accept', this.slotIdVal, this.slotIdVal).subscribe(data => {
      if (data && data.status === 1 && data.code === 2000) {
        if (callingFrom === 'from-detail') {
          this.meetingData.next(data);
          this.router.navigate(['/meeting/detail/', data.data.meetingDetail.meetingId]);
        } else {
          this.meeting.productId = data.data.meetingDetail.meetingId;
          this.meeting.tags = data.data.meetingDetail.tag;
          this.meeting.userStatus = data.data.meetingDetail.userStatus;
          this.meeting.rescheduleTimeSlots = data.data.meetingDetail.rescheduleTimeSlots;
          this.meeting.timeSlots = data.data.meetingDetail.timeSlots;
        }
        this.fcs.showCustomToast({
          toastType: ToastrType.SUCCESS,
          timeout: 5000,
          message: data.msg,
          title: 'Meeting Accepted By me'
        });

        this.closeModal('hideModal');
      }
      if (data && data.status === 0 && data.code === 2000) {
        this.fcs.showCustomToast({
          toastType: ToastrType.ERROR,
          timeout: 5000,
          message: data.error.msg,
          title: 'Error Occurred'
        });

      }
    });
  }

  /**
   * Calendar select date
   */
  setDate(dateObj: CalendarDateObject): void {
    const monthArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayArray = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayVal = dayArray[dateObj.Date.getDay()];
    const monthVal = monthArray[dateObj.Date.getMonth()];
    const dateVal = dateObj.Date.getDate();
    const yearVal = dateObj.Date.getFullYear();
    const onlyDate = dayVal + ' ' + monthVal + ' ' + dateVal + ' ' + yearVal;
    const currentDayVal = dayArray[new Date().getDay()];
    const currentMonthVal = monthArray[new Date().getMonth()];
    const currentDateVal = new Date().getDate();
    const currentYearVal = new Date().getFullYear();
    const currentOnlyDate = currentDayVal + ' ' + currentMonthVal + ' ' + currentDateVal + ' ' + currentYearVal;
    const currentFullTime = new Date().getHours() + ':' + new Date().getMinutes() + ':' + new Date().getSeconds();
    const currentHours = new Date().getHours();
    const timeZoneString = new Date().toTimeString().slice(9);
    const fullDate = onlyDate + ' ' + currentFullTime + ' ' + timeZoneString;
    this.rescheduleDateSelected = fullDate;
    if (onlyDate === currentOnlyDate) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newArray: any = this.timeValue.filter(x => (x.compareVal > currentHours));
      this.slotsAvailable = newArray;
      this.selectedOption = this.slotsAvailable[0].value;
    } else {
      this.slotsAvailable = this.timeValue;
      this.selectedOption = this.timeValue[0].value;
    }
    this.selectedOnlyDate = onlyDate;
    this.rescheduleFormSubmitted = false;
  }

  /**
   * Function used to set time slot while rescheduling a meeting
   */
  selectSlot(value: string = this.selectedOption): void {
    if (value !== this.timeValue[0].value) {
      this.rescheduleTimeSlot = true;
    } else {
      this.rescheduleTimeSlot = false;
    }
    const meetingStartTime = this.selectedOnlyDate + ' ' + value + ' ' + this.timezone;
    this.rescheduleMeetingTime.startTime = new Date(meetingStartTime).getTime();
    const meetingEndTime =
      this.rescheduleMeetingTime.startTime + ((this.rescheduleMeetingData.rescheduleData.slot.slotDifference) * 60 * 60 * 1000);
    this.rescheduleMeetingTime.endTime = meetingEndTime;
  }

  /**
   * Function used to get reason of rescheduling meeting
   */
  selectReason(idValue: string): void {
    // tslint:disable-next-line: radix
    const optionObj = this.rescheduleMeetingData.rescheduleData.reasons.find(data => data.id === parseInt(idValue));
    // tslint:disable-next-line: radix
    const reasonId = parseInt(idValue);
    this.rescheduleMeetingTime.reasonId = reasonId;
    if (reasonId === 3) {
      this.activeCommentForOtherOption = false;
      this.commentValue = '';
    } else {
      this.activeCommentForOtherOption = true;
      this.commentValue = optionObj.title;
    }
  }

  /**
   * Function used to send rescheduling meeting request
   */
  sendRescheduleRequest(): void {
    this.selectSlot(this.selectedOption);
    this.rescheduleMeetingTime.comment = this.commentValue;
    this.rescheduleMeetingTime.timezone = this.timeZoneTextString;
    if (this.meeting === undefined) {
      this.meetingId = this.meetingDetail.meetingId;
    } else {
      this.meetingId = this.meeting.productId;
    }

    this.rescheduleMeetingTime.meetingId = this.meetingId;
    this.rescheduleFormSubmitted = true;
    if (this.rescheduleDateSelected && this.rescheduleTimeSlot) {
      const meetingStartTime = this.rescheduleMeetingTime.startTime / 1000;
      const meetingEndTime = this.rescheduleMeetingTime.endTime / 1000;
      this.meetingRescheduleSubs = this.postSer.meetingReschedule(
        meetingStartTime,
        meetingEndTime,
        this.rescheduleMeetingTime.reasonId,
        this.rescheduleMeetingTime.comment,
        this.rescheduleMeetingTime.meetingId,
        this.rescheduleMeetingTime.timezone
      ).subscribe(data => {
        if (data && data.status === 1 && data.code === 2000) {
          this.meetingData.next(data);
          this.rescheduleFormSubmitted = false;
          this.fcs.showCustomToast({
            toastType: ToastrType.SUCCESS,
            timeout: 5000,
            message: data.msg,
            title: 'Meeting Rescheduled Successfully'
          });

          this.closeModal('hideModal');
        }
        if (data && data.status === 0 && data.code === 2000) {
          this.fcs.showCustomToast({
            toastType: ToastrType.ERROR,
            timeout: 5000,
            message: 'Meeting ' + data.error.msg,
            title: 'Error Occurred'
          });

        }
      });
    }
  }

  /**
   * Function used to unsubscribe all subscription
   */
  ngOnDestroy(): void {
    if (this.meetingRejectSubs) {
      this.meetingRejectSubs.unsubscribe();
    }
    if (this.meetingAcceptSubs) {
      this.meetingAcceptSubs.unsubscribe();
    }
    if (this.meetingRescheduleSubs) {
      this.meetingRescheduleSubs.unsubscribe();
    }
  }
}
